import { Drawer, Group, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus, IconRefresh } from "@tabler/icons-react"
import { OrganizationType } from "@tfg/db/browser"
import { Button, Loader, Select } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomFieldDefinitionValidationType } from "@tfg/types/schemas/customField"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { OrganizationsProvider } from "~/pages/Admin/MasterLists/CustomFields/contexts/useOrganizationsContext"
import { useAuth } from "~/store/auth-context"

import { useListOrganizationsQuery } from "../../Organizations/hooks"

import { CustomFieldsAdmin } from "./CustomFieldsAdmin"
import { CustomFieldsCreationForm } from "./CustomFieldsCreationForm"
import { SyncToProductsConfirmationModal } from "./SyncToProductsConfirmationModal"
import { CustomFieldsUpdateForm } from "./CustomFieldsUpdateForm"
import {
  useListCustomFieldsQuery,
  useSyncCustomFieldsToProductsMutation,
} from "./hooks"

export const CustomFieldsAdminWrapper = () => {
  const { user } = useAuth()
  const { t } = useTranslation("admin")
  const [organizationId, setOrganizationId] = useState<string | undefined>(
    user?.organization?.id
  )
  const customFieldsQuery = useListCustomFieldsQuery(organizationId)

  const { data: organizations, isPending: isLoading } =
    useListOrganizationsQuery({
      type: [OrganizationType.BRAND, OrganizationType.PLATFORM_ADMIN],
    })
  const selectOptions = organizations
    ? organizations.map((organization) => ({
        label: organization.name,
        value: organization.id.toString(),
      }))
    : []

  const [openedCreate, { open: openCreate, close: closeCreate }] =
    useDisclosure(false)
  const [openedUpdate, { open: openUpdate, close: closeUpdate }] =
    useDisclosure(false)
  const [
    openedSyncConfirmation,
    { open: openSyncConfirmation, close: closeSyncConfirmation },
  ] = useDisclosure(false)
  const { mutate: syncCustomFieldsToProducts, isPending: isSyncing } =
    useSyncCustomFieldsToProductsMutation(
      {
        organizationId: organizationId ?? "",
      },
      {
        onSuccess: () => {
          closeSyncConfirmation()
        },
      }
    )

  const [updatedCustomField, setUpdatedCustomField] = useState<
    CustomFieldDefinitionValidationType | undefined
  >(undefined)

  return (
    <OrganizationsProvider organizations={organizations ?? []}>
      {isLoading ? (
        <Loader fullPage />
      ) : (
        <Group justify="space-between">
          <Select
            data={selectOptions}
            value={organizationId ? organizationId.toString() : null}
            clearable
            searchable
            w={300}
            styles={{
              input: {
                backgroundColor: "white",
              },
            }}
            onChange={(value) => setOrganizationId(value ?? undefined)}
          />
          <Group>
            <Button
              onClick={() => {
                openCreate()
              }}
              icon={<IconPlus />}
            >
              New Custom Field
            </Button>
            <Button
              onClick={() => {
                openSyncConfirmation()
              }}
              disabled={!organizationId}
              loading={isSyncing}
              icon={<IconRefresh />}
            >
              Sync To Products
            </Button>
          </Group>
        </Group>
      )}
      <Drawer
        position="right"
        opened={openedCreate}
        onClose={closeCreate}
        size="lg"
        title={<Title order={2}>{t("customFields.create")}</Title>}
      >
        <CustomFieldsCreationForm close={closeCreate} />
      </Drawer>
      <Drawer
        position="right"
        opened={openedUpdate}
        onClose={closeUpdate}
        size="lg"
        title={<Title order={2}>{t("customFields.update")}</Title>}
      >
        {updatedCustomField && (
          <CustomFieldsUpdateForm
            close={closeUpdate}
            customField={updatedCustomField}
          />
        )}
      </Drawer>
      <SyncToProductsConfirmationModal
        opened={openedSyncConfirmation}
        onClose={closeSyncConfirmation}
        onConfirm={syncCustomFieldsToProducts}
        isLoading={isSyncing}
      />
      <DataFetchWrapper query={customFieldsQuery}>
        {({ data }) => (
          <CustomFieldsAdmin
            customFieldDefinitions={data}
            handleUpdate={(customField) => {
              setUpdatedCustomField(customField)
              openUpdate()
            }}
          />
        )}
      </DataFetchWrapper>
    </OrganizationsProvider>
  )
}
